import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { DimensionEnums } from "models/enums";
import { CurrencyCode } from "models/enums/currency";
import { InvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { InvoiceConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { useDimensionMaps } from "components/common";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, DateHelper, UtilsHelper } from "utils/helpers";

function useSelfInvoiceInitialValues(
  invoice: WintSuperkollAPIModel.SelfInvoice,
  draftCustomerInvoice?: WintSuperkollAPIModel.Invoice
) {
  const invoiceService = useInvoiceService();

  const loggedInUserId = useSelector((store: AppState) => store.auth.loggedInUser.personId);
  const noVat = useSelector((store: AppState) => store.auth.selectedCompany.noVat);

  const { dimensions, recommendedDimensions } = useDimensionMaps(DimensionEnums.DimensionCategory.Invoice);
  const allowedDimensionIds = React.useMemo(() => dimensions.flatMap((d) => d.value).map((d) => d.id), [dimensions]);

  const hasInvoiceRowDimensions = draftCustomerInvoice?.rows?.some(
    (row) => row.dimensions && row.dimensions.filter((d) => allowedDimensionIds.includes(d)).length > 0
  );

  const getInialValues = (): InvoiceFormTypes.SelfInvoice => {
    return draftCustomerInvoice
      ? {
          id: invoice.id,
          relatedInvoiceId: draftCustomerInvoice.id,
          ownerId: draftCustomerInvoice.ownerId,
          customer: draftCustomerInvoice.customerId
            ? { id: draftCustomerInvoice.customerId, value: draftCustomerInvoice.customerName ?? "" }
            : undefined,
          organizationNumber: draftCustomerInvoice.customerOrgNo?.replace("-", ""),
          invoiceDate: draftCustomerInvoice.postingDate,
          oCRNumber: draftCustomerInvoice.originOcrNumber,
          currency: draftCustomerInvoice.currency ?? CurrencyCode.SEK,
          products: draftCustomerInvoice.rows
            ?.sort((p1, p2) => p1.orderIndex - p2.orderIndex)
            .map((product): InvoiceFormTypes.Product => {
              const standardPrice = product.overriddenUnitPrice ?? product.unitPrice ?? 0;
              const vat = noVat
                ? 0
                : InvoiceConstants.VatTypes.includes(product.vat)
                  ? product.vat
                  : InvoiceConstants.DefaultVatType;

              return {
                id: product.id,
                uniqueId: product.id,
                article: { id: product.articleId, value: product.description ?? product.overriddenDescription ?? "" },
                quantity: product.quantity,
                text: product.overriddenDescription ?? product.description,
                price: standardPrice,
                unit: product.unit?.id,
                tax: vat,
                taxText:
                  vat === 0
                    ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.VATFree)
                    : `${product.vat}%`,
                totalAmountExTax: invoiceService.calculateAmount(standardPrice ?? 0, product.quantity ?? 0),
                eUVatCategoryId: product.euVatProductType,
                dimensions: product.dimensions
                  ? product.dimensions
                      .filter((d) => allowedDimensionIds.includes(d))
                      .concat(
                        draftCustomerInvoice.dimensions?.filter(
                          (d) => allowedDimensionIds.includes(d) && !product.dimensions?.includes(d)
                        ) ?? []
                      )
                  : draftCustomerInvoice.dimensions?.filter((d) => allowedDimensionIds.includes(d)),
                periodisation: {
                  isEnabled: product.periodisation,
                  range: product.periodisation
                    ? {
                        start: DateHelper.getDateFromYearMonth(
                          (product.periodisationStartYear ?? 0) * 100 + (product.periodisationStartMonth ?? 0)
                        ),
                        end: DateHelper.getDateFromYearMonth(
                          (product.periodisationEndYear ?? 0) * 100 + (product.periodisationEndMonth ?? 0)
                        ),
                      }
                    : undefined,
                },
              };
            }),
          isPeriodisationAdvancedConfiguration: draftCustomerInvoice.rows?.some(
            (row, index, arr) =>
              `${row.periodisation}${row.periodisationStartYear}${row.periodisationStartMonth}${row.periodisationEndYear}${row.periodisationEndMonth}` !==
              `${arr[0].periodisation}${arr[0].periodisationStartYear}${arr[0].periodisationStartMonth}${arr[0].periodisationEndYear}${arr[0].periodisationEndMonth}`
          ),
          isDimensionsAdvancedConfiguration: hasInvoiceRowDimensions,
        }
      : {
          id: invoice.id,
          ownerId: loggedInUserId,
          customer: invoice.customerId ? { id: invoice.customerId, value: invoice.customerName ?? "" } : undefined,
          organizationNumber: invoice.customerOrgNr?.replace("-", ""),
          invoiceDate: invoice.invoiceDate,
          oCRNumber: invoice.oCRNumber,
          currency: invoice.currency ?? CurrencyCode.SEK,
          products: invoice.items?.map((i, index): InvoiceFormTypes.Product => {
            const description = !UtilsHelper.isEmpty(i.text) ? i.text : `Selfinvoice item ${index}`;
            const vatRate = noVat
              ? 0
              : InvoiceConstants.VatTypes.includes(i.vatRate)
                ? i.vatRate
                : InvoiceConstants.DefaultVatType;
            const isMissingUnitAndQuantity = !i.unit?.id && !i.quantity;

            return {
              guid: i.id,
              uniqueId: i.id,
              article: { id: 0, value: description! },
              quantity: isMissingUnitAndQuantity ? 1 : (i.quantity ?? 0),
              text: description,
              price: isMissingUnitAndQuantity && i.totalAmountExVAT ? i.totalAmountExVAT : (i.unitPrice ?? 0),
              unit: i.unit?.id,
              tax: vatRate,
              taxText:
                vatRate === 0
                  ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.VATFree)
                  : `${vatRate}%`,
              totalAmountExTax: i.totalAmountExVAT,
              dimensions: recommendedDimensions.filter((d) => allowedDimensionIds.includes(d)),
            };
          }),
          isPeriodisationAdvancedConfiguration: false,
          isDimensionsAdvancedConfiguration: false,
        };
  };

  const getTodoInitialValues = (): InvoiceFormTypes.SelfInvoiceTodo => {
    const baseInitialValues = getInialValues();
    return {
      ...baseInitialValues,
      dimensions:
        !draftCustomerInvoice || baseInitialValues.isDimensionsAdvancedConfiguration
          ? []
          : invoiceService
              .getInvoiceDimensionData(invoice.totalAmountExVat ?? 0, draftCustomerInvoice.dimensions)
              ?.filter((d) => allowedDimensionIds.includes(d.dimensionId)),
    };
  };

  return { getInialValues, getTodoInitialValues };
}

export default useSelfInvoiceInitialValues;
